import {FC, useEffect, useState} from 'react';

import {
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Checkbox,
  ListItemText,
  FormControl,
  FormControlProps,
  useTheme,
  Typography,
  InputLabelProps,
  SelectChangeEvent,
} from '@mui/material';

import {getFieldStyle} from '../../layouts/theme/FieldStyles';
import {Option} from '../../types/shared-types';

interface SelectOption extends Option {
  selected?: boolean;
}

interface EmpionMultiSelectProps {
  label: string;
  options: SelectOption[];
  onChangeHandler: (options: SelectOption[]) => void;
  backgroundVariant?: 'light' | 'dark';
  displayDefaultOptions?: any[];
}

export const MultiSelectV2: FC<
  EmpionMultiSelectProps & Omit<SelectProps, 'variant' | 'onChange'> & FormControlProps & InputLabelProps
> = ({label, options, onChangeHandler, backgroundVariant, ...props}) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    // Resets selected options if available list of options changes.
    setSelectedOptions([...options.filter((opt) => !!opt.selected)]);
  }, [options]);

  const onSelect = (event: SelectChangeEvent<SelectOption[]>) => {
    const values = event.target.value as SelectOption[];
    let duplicateId: string | undefined;
    const ids: string[] = [];

    values.forEach((opt) => {
      if (ids.includes(opt.id)) {
        duplicateId = opt.id;
      } else {
        ids.push(opt.id);
      }
    });
    const updatedOptions = duplicateId ? values.filter((opt) => opt.id !== duplicateId) : values;
    setSelectedOptions(updatedOptions);
    onChangeHandler(updatedOptions as SelectOption[]);
  };

  const theme = useTheme();

  // Material UI MultiSelect default styles
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      },
    },
  };

  return (
    <div>
      <FormControl
        sx={{
          ...getFieldStyle(theme, backgroundVariant),
          width: '100%',
        }}
      >
        <InputLabel
          sx={{
            marginTop: '-8px',
            '&.MuiInputLabel-shrink': {
              marginTop: '0px',
            },
          }}
          id='empion-multiple-checkbox-label'
        >
          <Typography>{label.charAt(0).toUpperCase() + label.slice(1)}</Typography>
        </InputLabel>
        <Select
          size='medium'
          labelId='empion-multiple-checkbox-label'
          id='empion-multiple-checkbox'
          {...props}
          label={label}
          multiple
          value={selectedOptions}
          defaultValue={undefined}
          onChange={onSelect}
          renderValue={(selected: SelectOption[]) => selected.map((opt) => opt.label).join(', ')}
          MenuProps={MenuProps}
        >
          {options?.map((option) => (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            <MenuItem key={option.id} value={option} disabled={!option.id}>
              <Checkbox
                sx={{
                  color: theme.palette.text.primary,
                  '&.Mui-checked': {
                    color: theme.palette.text.primary,
                  },
                }}
                checked={selectedOptions.some((entry) => entry.id === option.id)}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
